<div class="container py-3">
  <h2 class="mb-3">{{ title }}</h2>

  <div class="alert alert-info">
    <p class="mb-1">
      RTK Base Analyzer (Pro Edition) lets you analyze RTCM data recorded from your external NTRIP mount points that are configured under
      <a class="link-primary" routerLink="/ext-mountpoint-connections">External MountPoint Connections</a>.
    </p>
    <p class="mb-0">
      Select a mount point, choose the analysis duration, and submit the task. Once the analysis completes, refresh the result to review the latest report.
    </p>
  </div>

  <div class="card shadow-sm">
    <div class="card-body">
      <div class="row g-3 align-items-end mb-3">
        <div class="col-md-6">
          <label class="form-label small">Search (mount point, downloader user, server)</label>
          <div class="input-group input-group-sm">
            <input type="text" class="form-control" [(ngModel)]="search" placeholder="Search keyword">
            <button class="btn btn-outline-primary" type="button" (click)="onSearch()">Search</button>
          </div>
        </div>
        <div class="col-md-6 text-md-end">
          <div *ngIf="isLoading" class="text-muted small">
            Loading connections...
          </div>
          <div *ngIf="!isLoading && !hasConnections" class="text-muted small">
            No mount point connections found.
          </div>
        </div>
      </div>

      <div *ngIf="errorMessage" class="alert alert-danger py-2">
        {{ errorMessage }}
      </div>

      <ng-container *ngIf="hasConnections">
        <div class="d-flex justify-content-between align-items-center mt-2 mb-3">
          <div><strong>Mount Point:</strong> {{ currentPage }} / {{ totalPages }}</div>
          <div class="btn-toolbar" role="toolbar" aria-label="pagination">
            <div class="btn-group me-2" role="group">
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="goFirst()" [disabled]="currentPage === 1">« First</button>
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="goPrev()" [disabled]="currentPage === 1">‹ Prev</button>
            </div>
            <div class="btn-group me-2" role="group">
              <button *ngFor="let p of pageWindow" type="button"
                      class="btn btn-sm"
                      [ngClass]="{'btn-primary': p === currentPage, 'btn-outline-secondary': p !== currentPage}"
                      (click)="goToPage(p)">{{ p }}</button>
            </div>
            <div class="btn-group" role="group">
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="goNext()" [disabled]="currentPage === totalPages">Next ›</button>
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="goLast()" [disabled]="currentPage === totalPages">Last »</button>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-end align-items-center mb-3">
          <label class="me-2 mb-0 small">Select a Mount Point:</label>
          <div class="mp-select-wrapper" style="min-width: 280px;">
            <select class="form-select form-select-sm"
                    [ngModel]="getConnectionKey(currentConnection)"
                    (ngModelChange)="selectFromDropdown($event)">
              <option value=""></option>
              <option *ngFor="let c of sortedConnections" [value]="getConnectionKey(c)">
                {{ c.mountPoint }}
              </option>
            </select>
          </div>
        </div>

        <div class="table-responsive pro-table mb-4">
          <table class="table table-sm table-hover align-middle">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Mount Point</th>
                <th scope="col">Server</th>
                <th scope="col">Port</th>
                <th scope="col">Downloader User</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let c of sortedConnections; let i = index"
                  (click)="goToConnection(c)"
                  [class.table-active]="getConnectionKey(c) === getConnectionKey(selectedConnection)">
                <td>{{ i + 1 }}</td>
                <td>{{ c.mountPoint }}</td>
                <td>{{ c.server }}</td>
                <td>{{ c.port }}</td>
                <td>{{ c.downloaderUserName }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div *ngIf="selectedConnection as conn" class="mp-card">
          <div class="row g-3">
            <div class="col-12 col-lg-7">
              <div class="alert alert-light border mb-3">
                <div class="row g-2">
                  <div class="col-sm-6">
                    <div class="label">Mount Point</div>
                    <div class="value">{{ conn.mountPoint }}</div>
                  </div>
                  <div class="col-sm-6">
                    <div class="label">Server</div>
                    <div class="value">{{ conn.server }}:{{ conn.port }}</div>
                  </div>
                  <div class="col-sm-6">
                    <div class="label">Downloader User</div>
                    <div class="value">{{ conn.downloaderUserName }}</div>
                  </div>
                  <div class="col-sm-6">
                    <div class="label">Downloader Password</div>
                    <div class="value">{{ conn.downloaderPassword }}</div>
                  </div>
                </div>
              </div>

              <div class="subsection">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h4 class="subsection-title m-0">Analyze RTK Base</h4>
                  <div class="text-muted small" *ngIf="analysisStatusByMountpoint[conn.mountPoint || '']">
                    {{ analysisStatusByMountpoint[conn.mountPoint || ''] }}
                  </div>
                </div>
                <div class="row g-2">
                  <div class="col-md-5">
                    <label class="form-label small">Analysis Duration</label>
                    <select class="form-select form-select-sm"
                            [ngModel]="getAnalysisDurationSelection(conn)"
                            (ngModelChange)="onAnalysisDurationChange(conn, $event)">
                      <option *ngFor="let opt of durationPresetOptions" [ngValue]="opt.minutes">{{ opt.label }}</option>
                    </select>
                  </div>
                </div>
                <div class="d-flex align-items-center mt-3">
                  <button class="btn btn-sm btn-primary"
                          (click)="onStartAnalysis(conn)"
                          [disabled]="analysisLoadingByMountpoint[conn.mountPoint || '']">
                    {{ hasAnalysisResult(conn) ? 'Re-analyze this RTK Base' : 'Analyze this RTK Base' }}
                  </button>
                  <button class="btn btn-sm btn-outline-secondary ms-2"
                          (click)="onRefreshAnalysisResult(conn)"
                          [disabled]="analysisFetchingByMountpoint[conn.mountPoint || '']">
                    Refresh analysis result
                  </button>
                </div>
              </div>

              <div class="row mt-3 subsection" *ngIf="showSnr && hasAnalyzerSummary(conn.mountPoint || '') && (analyzerSummaryByMountpoint[conn.mountPoint || '']?.['signal_quality']?.grade || analyzerSummaryByMountpoint[conn.mountPoint || '']?.['Average'] !== undefined)">
                <div class="col-12">
                  <div class="d-flex justify-content-between align-items-center mb-2 subheader">
                    <h4 class="m-0 subsection-title">Satellite Signal Quality</h4>
                    <div class="ms-auto d-flex align-items-center gap-3">
                      <button type="button" class="btn btn-sm btn-link text-decoration-none chevron-btn"
                              (click)="toggleSnrSection(conn.mountPoint || '')"
                              [attr.aria-expanded]="!isSnrCollapsed(conn.mountPoint || '')"
                              aria-label="Toggle SNR">
                        <span class="material-icons" *ngIf="isSnrCollapsed(conn.mountPoint || '')">chevron_right</span>
                        <span class="material-icons" *ngIf="!isSnrCollapsed(conn.mountPoint || '')">expand_more</span>
                      </button>
                    </div>
                  </div>
                </div>
                <div class="col-12" *ngIf="!isSnrCollapsed(conn.mountPoint || '') && analyzerSummaryByMountpoint[conn.mountPoint || ''] as snrSummary">
                  <div class="mp-meta mb-2">
                    <div>Signal Quality:
                      <span class="mp-value">{{ snrSummary?.signal_quality?.grade || '—' }}</span>
                    </div>
                    <div *ngIf="snrSummary?.signal_quality?.metrics?.sat_cn0_stat?.median_of_sat_medians !== undefined">
                      Satellite Signal C/N₀ Median (dB‑Hz):
                      <span class="mp-value">{{ snrSummary.signal_quality.metrics.sat_cn0_stat.median_of_sat_medians | number:'1.1-1' }}</span>
                    </div>
                  </div>
                  <div class="snr-chart" [attr.data-mp]="conn.mountPoint">
                    <svg [attr.id]="getSnrSvgId(conn.mountPoint || '')" style="width: 100%; height: 100%;"></svg>
                  </div>
                </div>
              </div>

              <div class="row mt-3 subsection" *ngIf="showMap && hasAnalyzerSummary(conn.mountPoint || '')">
                <div class="col-12">
                  <div class="d-flex justify-content-between align-items-center mb-2 subheader">
                    <h4 class="m-0 subsection-title">RTK Base Station Location</h4>
                    <div class="ms-auto d-flex align-items-center gap-3">
                      <button type="button" class="btn btn-sm btn-link text-decoration-none chevron-btn"
                              (click)="toggleMapSection(conn.mountPoint || '')"
                              [attr.aria-expanded]="!isMapCollapsed(conn.mountPoint || '')"
                              aria-label="Toggle map">
                        <span class="material-icons" *ngIf="isMapCollapsed(conn.mountPoint || '')">chevron_right</span>
                        <span class="material-icons" *ngIf="!isMapCollapsed(conn.mountPoint || '')">expand_more</span>
                      </button>
                    </div>
                  </div>
                </div>
                <div class="col-12" *ngIf="!isMapCollapsed(conn.mountPoint || '') && analyzerSummaryByMountpoint[conn.mountPoint || ''] as mapSummary">
                  <div class="mp-meta mb-2">
                    <div>RTK Base Coordinate: <span class="mp-value">{{ mapSummary['location'] }}</span></div>
                    <div>RTK base Geodetic Height (meter): <span class="mp-value">{{ mapSummary['geodetic_height_meters'] }}</span></div>
                    <div>RTK base Orthometric Height (meter): <span class="mp-value">{{ mapSummary['orthometric_height_meters'] }}</span></div>
                  </div>
                </div>
                <div class="col-12" *ngIf="!isMapCollapsed(conn.mountPoint || '')">
                  <div class="map-box" [id]="getMapElementId(conn.mountPoint || '')"></div>
                </div>
                <div class="col-12" *ngIf="!isMapCollapsed(conn.mountPoint || '')">
                  <div class="ext-map-grid w-100" style="grid-template-columns: repeat(3, 1fr);">
                    <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getGoogleMapUrl(conn.mountPoint || '', 'sat')">Google Satellite</a>
                    <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getGoogleMapUrl(conn.mountPoint || '', 'map')">Google Street</a>
                    <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getBingMapUrl(conn.mountPoint || '', 'sat')">Bing Satellite</a>
                    <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getBingMapUrl(conn.mountPoint || '', 'map')">Bing Street</a>
                    <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getAmapUrl(conn.mountPoint || '', 'map')">Amap Street & Satellite</a>
                    <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getBaiduUrl(conn.mountPoint || '', 'map')">Baidu Street & Satellite</a>
                  </div>
                </div>
              </div>

              <div class="analysis-result mt-3">
                <div class="fw-semibold mb-2">Analysis Result</div>
                <div class="d-flex gap-2 mb-2" *ngIf="hasAnalysisResult(conn)">
                  <button class="btn btn-sm btn-outline-secondary" (click)="expandAll(conn.mountPoint || '')">Expand All</button>
                  <button class="btn btn-sm btn-outline-secondary" (click)="collapseAll(conn.mountPoint || '')">Collapse All</button>
                  <button class="btn btn-sm btn-outline-secondary" (click)="copyJson(conn)">Copy JSON</button>
                </div>
                <ng-container *ngIf="analysisResultsByMountpoint[conn.mountPoint || ''] as json; else noAnalysisResult">
                  <div class="json-tree">
                    <ng-template #renderNode let-key="key" let-value="value" let-path="path" let-mp="mp">
                      <div class="json-row" *ngIf="!isObject(value) && !isArray(value)">
                        <span class="json-key">{{ key }}:</span>
                        <a *ngIf="isUrl(value)" class="json-value" [href]="value" target="_blank" rel="noopener">{{ value }}</a>
                        <span *ngIf="!isUrl(value)" class="json-value">{{ value }}</span>
                      </div>
                      <div class="json-node" *ngIf="isObject(value)">
                        <div class="json-key clickable" (click)="toggleNode(mp, path)">
                          {{ isExpanded(mp, path) ? '▼' : '▶' }} {{ key }}:
                        </div>
                        <div class="json-children" *ngIf="isExpanded(mp, path)">
                          <ng-container *ngFor="let child of value | keyvalue:keyOrder">
                            <ng-container *ngTemplateOutlet="renderNode; context: { key: child.key, value: child.value, path: path + '.' + child.key, mp: mp }"></ng-container>
                          </ng-container>
                        </div>
                      </div>
                      <div class="json-array" *ngIf="isArray(value)">
                        <div class="json-key clickable" (click)="toggleNode(mp, path)">
                          {{ isExpanded(mp, path) ? '▼' : '▶' }} {{ key }}:
                        </div>
                        <ul class="json-ul" *ngIf="isExpanded(mp, path)">
                          <li *ngFor="let v of value; let idx = index">
                            <ng-container *ngTemplateOutlet="renderNode; context: { key: idx, value: v, path: path + '[' + idx + ']', mp: mp }"></ng-container>
                          </li>
                        </ul>
                      </div>
                    </ng-template>

                    <ng-container *ngIf="isObject(json); else renderRootArray">
                      <ng-container *ngFor="let item of json | keyvalue:keyOrder">
                        <ng-container *ngTemplateOutlet="renderNode; context: { key: item.key, value: item.value, path: item.key, mp: conn.mountPoint || '' }"></ng-container>
                      </ng-container>
                    </ng-container>
                    <ng-template #renderRootArray>
                      <div class="json-array">
                        <ul class="json-ul">
                          <li *ngFor="let v of json; let idx = index">
                            <ng-container *ngTemplateOutlet="renderNode; context: { key: idx, value: v, path: '[' + idx + ']', mp: conn.mountPoint || '' }"></ng-container>
                          </li>
                        </ul>
                      </div>
                    </ng-template>
                  </div>
                </ng-container>
                <ng-template #noAnalysisResult>
                  <div class="text-muted small">
                    No analysis report available yet. Submit a new analysis or refresh the status after a few minutes.
                  </div>
                </ng-template>
              </div>
            </div>
            <div class="col-12 col-lg-5 d-flex align-items-start">
              <img src="assets/images/landing/GeoAstra_RTK_Base_Analyzer_diagram.drawio.png"
                   alt="RTK Base Analyzer Diagram"
                   class="img-fluid w-100 rounded border diagram-scale-80 diagram-align-top">
            </div>
          </div>
        </div>
      </ng-container>
    </div>
  </div>
</div>


